import { scenarioModeKeys } from "./defaults";

export const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const safeDivide = (numerator, denominator) => {
  const num = toNumber(numerator, 0);
  const den = toNumber(denominator, 0);
  if (den <= 0) return 0;
  return num / den;
};

const toPct = (value) => toNumber(value, 0) / 100;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const calcLoadedHourlyRate = (salary, assumptions) => {
  const annualSalary = toNumber(salary, 0);
  const niRate = toPct(assumptions.employerNiRatePct);
  const niThreshold = toNumber(assumptions.employerNiThreshold, 0);
  const pensionRate = toPct(assumptions.employerPensionRatePct);
  const overheadRate = toPct(assumptions.additionalOverheadPct);
  const weeklyHours = toNumber(assumptions.weeklyContractedHours, 0);
  const productiveWeeks = toNumber(assumptions.productiveWeeksPerYear, 0);

  const employerNi = Math.max(annualSalary - niThreshold, 0) * niRate;
  const employerPension = annualSalary * pensionRate;
  const additionalOverhead = annualSalary * overheadRate;
  const annualEmploymentCost = annualSalary + employerNi + employerPension + additionalOverhead;
  const productiveAnnualHours = weeklyHours * productiveWeeks;
  const salaryOnlyHourlyRate = safeDivide(annualSalary, productiveAnnualHours);
  const loadedHourlyRate = safeDivide(annualEmploymentCost, productiveAnnualHours);

  return {
    annualSalary,
    employerNi,
    employerPension,
    additionalOverhead,
    annualEmploymentCost,
    productiveAnnualHours,
    salaryOnlyHourlyRate,
    loadedHourlyRate,
  };
};

const getAppointmentsRequired = (values) => {
  const explicit = toNumber(values.appointmentsRequired, 0);
  if (explicit > 0) return explicit;
  return Math.max(toNumber(values.vacanciesToFill, 0), 0);
};

const getConventionalInterviewed = (values) => {
  const explicit = toNumber(values.conventionalInterviewed, 0);
  if (explicit > 0) return explicit;
  return Math.max(toNumber(values.conventionalInterviewAttending, 0), 0);
};

const interviewDays = (interviewed, candidatesPerInterviewDay) =>
  safeDivide(interviewed, toNumber(candidatesPerInterviewDay, 0));

const calculateConventionalRoute = ({ values, hrRate, operationalRate }) => {
  const reviewed = toNumber(values.conventionalGatewayPass, toNumber(values.conventionalApplicationsManualReview, 0));
  const interviewed = getConventionalInterviewed(values);
  const appointments = getAppointmentsRequired(values);
  const interviewDayEq = interviewDays(interviewed, values.candidatesPerInterviewDay);

  const hrPanelHours =
    interviewDayEq *
    toNumber(values.interviewDayHours, 0) *
    toNumber(values.hrPanelMembers, 0);

  const operationalPanelHours =
    interviewDayEq *
    toNumber(values.interviewDayHours, 0) *
    toNumber(values.operationalPanelMembers, 0);

  const recruitmentHours =
    toNumber(values.gatewaySetupHours, 0) +
    safeDivide(reviewed * toNumber(values.conventionalReviewMinutes, 0), 60) +
    toNumber(values.fixedSchedulingHours, 0) +
    safeDivide(interviewed * toNumber(values.schedulingMinutesPerInterview, 0), 60) +
    appointments * toNumber(values.offerProgressionHours, toNumber(values.conventionalOfferProgressionHours, 0)) +
    hrPanelHours;

  const operationalHours = toNumber(values.conventionalOpsShortlistHours, 0) + operationalPanelHours;
  const totalStaffHours = recruitmentHours + operationalHours;

  const recruitmentLabourValue = recruitmentHours * hrRate;
  const operationalLabourValue = operationalHours * operationalRate;
  const internalLabourValue = recruitmentLabourValue + operationalLabourValue;

  const externalCashCosts = toNumber(values.otherConventionalCampaignCosts, 0);
  const totalModelledCost = internalLabourValue + externalCashCosts;
  const costPerAppointment = safeDivide(totalModelledCost, appointments);

  return {
    route: "conventional",
    reviewVolume: reviewed,
    interviewed,
    interviewDays: interviewDayEq,
    exactInterviewDays: interviewDayEq,
    bookedInterviewDays: Math.ceil(interviewDayEq),
    recruitmentHours,
    operationalHours,
    otherPanelHours: 0,
    totalStaffHours,
    recruitmentLabourValue,
    operationalLabourValue,
    otherPanelLabourValue: 0,
    internalLabourValue,
    externalCashCosts,
    formalAssessmentCost: 0,
    cashCosts: {
      totalCashCosts: externalCashCosts,
      railwayCareersSuccessFee: 0,
    },
    totalModelledCost,
    costPerAppointment,
    costPerSuccessfulHire: costPerAppointment,
  };
};

const calculateTalentRoute = ({ values, hrRate, operationalRate }) => {
  const reviewed = toNumber(values.talentProfilesReviewed, 0);
  const interviewed = toNumber(values.talentInterviewed, 0);
  const appointments = getAppointmentsRequired(values);
  const interviewDayEq = interviewDays(interviewed, values.candidatesPerInterviewDay);

  const hrPanelHours =
    interviewDayEq *
    toNumber(values.interviewDayHours, 0) *
    toNumber(values.hrPanelMembers, 0);

  const operationalPanelHours =
    interviewDayEq *
    toNumber(values.interviewDayHours, 0) *
    toNumber(values.operationalPanelMembers, 0);

  const recruitmentHours =
    toNumber(values.talentRouteSetupHours, toNumber(values.talentSearchSetupHours, 0)) +
    safeDivide(reviewed * toNumber(values.talentReviewMinutes, 0), 60) +
    toNumber(values.talentFixedSchedulingHours, 0) +
    safeDivide(interviewed * toNumber(values.schedulingMinutesPerInterview, 0), 60) +
    appointments * toNumber(values.offerProgressionHours, toNumber(values.talentOfferProgressionHours, 0)) +
    hrPanelHours;

  const operationalHours = toNumber(values.talentOpsShortlistHours, 0) + operationalPanelHours;
  const totalStaffHours = recruitmentHours + operationalHours;

  const recruitmentLabourValue = recruitmentHours * hrRate;
  const operationalLabourValue = operationalHours * operationalRate;
  const internalLabourValue = recruitmentLabourValue + operationalLabourValue;

  const railwayCareersSuccessFee =
    toNumber(values.successfulRailwayHires, 0) * toNumber(values.feePerSuccessfulHire, 0);

  const accessCost = toNumber(values.talentPoolAccessCostPerCampaign, 0);
  const externalCashCosts = accessCost + railwayCareersSuccessFee;
  const totalModelledCost = internalLabourValue + externalCashCosts;
  const costPerAppointment = safeDivide(totalModelledCost, appointments);

  return {
    route: "talentPool",
    reviewVolume: reviewed,
    interviewed,
    interviewDays: interviewDayEq,
    exactInterviewDays: interviewDayEq,
    bookedInterviewDays: Math.ceil(interviewDayEq),
    recruitmentHours,
    operationalHours,
    otherPanelHours: 0,
    totalStaffHours,
    recruitmentLabourValue,
    operationalLabourValue,
    otherPanelLabourValue: 0,
    internalLabourValue,
    externalCashCosts,
    formalAssessmentCost: 0,
    cashCosts: {
      totalCashCosts: externalCashCosts,
      railwayCareersSuccessFee,
    },
    totalModelledCost,
    costPerAppointment,
    costPerSuccessfulHire: costPerAppointment,
  };
};

export const buildModeAdjustedInputs = (values, mode = "expected") => {
  const prefix = mode.toLowerCase();
  const attendancePct = clamp(toNumber(values[`${prefix}InterviewAttendancePct`], 100), 0, 100);
  const interviewToHirePct = clamp(toNumber(values[`${prefix}InterviewToHirePct`], 100), 0, 100);
  const conventionalInterviewVolumePct = Math.max(toNumber(values[`${prefix}ConventionalInterviewVolumePct`], 100), 0);
  const operationalShortlistTimePct = Math.max(toNumber(values[`${prefix}OperationalShortlistTimePct`], 100), 0);

  const talentInterviewed = Math.round(toNumber(values.talentInterviewed, 0) * (attendancePct / 100));
  const successfulRailwayHires = Math.round(talentInterviewed * (interviewToHirePct / 100));

  return {
    ...values,
    successfulRailwayHires,
    conventionalInterviewed: Math.round(getConventionalInterviewed(values) * (conventionalInterviewVolumePct / 100)),
    conventionalOpsShortlistHours: toNumber(values.conventionalOpsShortlistHours, 0) * (operationalShortlistTimePct / 100),
    talentOpsShortlistHours: toNumber(values.talentOpsShortlistHours, 0) * (operationalShortlistTimePct / 100),
  };
};

export const calculateBusinessCaseFromInputs = (values) => {
  const hrRates = calcLoadedHourlyRate(values.hrAnnualSalary, values);
  const operationalRates = calcLoadedHourlyRate(values.operationalAnnualSalary, values);
  const otherPanelRates = calcLoadedHourlyRate(values.otherPanelAnnualSalary, values);

  const conventional = calculateConventionalRoute({
    values,
    hrRate: hrRates.loadedHourlyRate,
    operationalRate: operationalRates.loadedHourlyRate,
  });

  const talent = calculateTalentRoute({
    values,
    hrRate: hrRates.loadedHourlyRate,
    operationalRate: operationalRates.loadedHourlyRate,
  });

  const internalCapacityReleasedHours = conventional.totalStaffHours - talent.totalStaffHours;
  const internalCapacityValue = conventional.internalLabourValue - talent.internalLabourValue;
  const talentPoolFee = talent.cashCosts.railwayCareersSuccessFee + toNumber(values.talentPoolAccessCostPerCampaign, 0);
  const perCampaignNetValue = internalCapacityValue - talentPoolFee;
  const annualisedValue = perCampaignNetValue * toNumber(values.campaignsPerYear, 0);
  const interviewPanelDaysAvoided = conventional.interviewDays - talent.interviewDays;
  const workingPersonDaysReleased = safeDivide(internalCapacityReleasedHours, toNumber(values.interviewDayHours, 0));
  const operationalManagerDaysReleased = safeDivide(
    conventional.operationalHours - talent.operationalHours,
    toNumber(values.interviewDayHours, 0)
  );

  const valuePerPound = talentPoolFee > 0 ? safeDivide(internalCapacityValue, talentPoolFee) : 0;
  const percentageCostReduction = safeDivide(perCampaignNetValue, conventional.totalModelledCost) * 100;

  return {
    hrRates,
    operationalRates,
    otherPanelRates,
    conventional,
    talent,
    summary: {
      appointmentsRequired: getAppointmentsRequired(values),
      successfulRailwayHires: toNumber(values.successfulRailwayHires, 0),
      feePerSuccessfulHire: toNumber(values.feePerSuccessfulHire, 0),
      railwayCareersSuccessFee: talent.cashCosts.railwayCareersSuccessFee,
      talentPoolAccessCostPerCampaign: toNumber(values.talentPoolAccessCostPerCampaign, 0),
      talentPoolCostPerCampaign: talentPoolFee,
      internalCapacityReleasedHours,
      totalStaffHoursReleased: internalCapacityReleasedHours,
      internalCapacityValue,
      perCampaignNetValue,
      netModelledValue: perCampaignNetValue,
      annualisedValue,
      percentageCostReduction,
      interviewPanelDaysAvoided,
      bookedInterviewDaysAvoided: conventional.bookedInterviewDays - talent.bookedInterviewDays,
      workingPersonDaysReleased,
      operationalManagerDaysReleased,
      costPerAppointmentConventional: conventional.costPerAppointment,
      costPerAppointmentTalent: talent.costPerAppointment,
      valuePerPound,
    },
  };
};

export const calculateBusinessCaseForMode = (values, mode = "expected") => {
  const adjusted = buildModeAdjustedInputs(values, mode);
  const result = calculateBusinessCaseFromInputs(adjusted);
  return {
    ...result,
    mode,
    adjustedValues: adjusted,
  };
};

export const calculateScenarioResults = (values) => {
  const results = {};
  scenarioModeKeys.forEach((mode) => {
    results[mode] = calculateBusinessCaseForMode(values, mode);
  });

  const netValues = scenarioModeKeys.map((mode) => results[mode].summary.perCampaignNetValue);
  return {
    results,
    range: {
      minNet: Math.min(...netValues),
      maxNet: Math.max(...netValues),
    },
  };
};

export const calculateBusinessCase = (values) => calculateBusinessCaseForMode(values, "expected");

export const formatCurrencyGbp = (value, digits = 0) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(toNumber(value, 0));

export const formatNumber = (value, digits = 0) =>
  new Intl.NumberFormat("en-GB", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(toNumber(value, 0));

export const formatPercent = (value, digits = 1) => `${formatNumber(value, digits)}%`;

export const ukDate = (isoDate) => {
  if (!isoDate) return "Not specified";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "Not specified";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
